import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { ErrorState } from '../../../media/shared/ErrorState.js';
import { MediaCard } from '../../../media/shared/MediaCard.js';
import {
  type MediaColumn,
  MediaTable,
  type MediaTableRow,
} from '../../../media/shared/MediaTable.js';
import { NoData } from '../../../media/shared/NoData.js';
import { formatMetricValue } from '../../data/format.js';
import { MetricDelta } from '../../MetricDelta.js';
import { ELLIPSIS_SX } from '../../utils.js';
import { type CreativeDetailScope, type DeliveryOutput, useDelivery } from '../data/api.js';
import { SectionHeader } from '../shared/SectionHeader.js';
import { SectionSkeleton } from '../shared/SectionSkeleton.js';
import { TrendMetricPicker } from '../shared/TrendMetricPicker.js';
import { CreativeTrendChart } from './CreativeTrendChart.js';
import { buildDeliveryTree, type DeliveryTreeNode, expandableNodeIds } from './deliveryTree.js';

const LEVEL_LABELS: Record<DeliveryTreeNode['row']['level'], string> = {
  campaign: 'Campaign',
  adset: 'Ad set',
  ad: 'Ad',
};

const CHILD_GROUP_LABELS: Record<DeliveryTreeNode['row']['level'], string | null> = {
  campaign: 'Ad sets',
  adset: 'Ads',
  ad: null,
};

const ZOOM_DIALOG_SX = { width: 720, maxWidth: '90vw' };
const SECTION_TITLE = 'Delivery';
const SECTION_CAPTION = 'Click any level to chart it';
const COPIED_ICON_MS = 1500;
const EXPANDER_SIZE = 28;

interface DeliverySectionProps {
  scope: CreativeDetailScope;
  creativeId: string;
  kpis: string[];
  onNodeSelect: (node: { level: 'campaign' | 'adset' | 'ad'; id: string } | undefined) => void;
}

function NameCell({ name }: { name: string }): ReactNode {
  return (
    <Tooltip title={name} placement="top">
      <Typography sx={{ fontSize: 13, fontWeight: 500, minWidth: 0, ...ELLIPSIS_SX }}>
        {name}
      </Typography>
    </Tooltip>
  );
}

function visibleRows(
  nodes: DeliveryTreeNode[],
  depth: number,
  expanded: Set<string>,
): { node: DeliveryTreeNode; depth: number }[] {
  return nodes.flatMap((node) => {
    const entry = { node, depth };
    if (node.children.length === 0 || !expanded.has(node.row.id)) return [entry];
    return [entry, ...visibleRows(node.children, depth + 1, expanded)];
  });
}

function deliveryMetricCells(
  node: DeliveryTreeNode,
  columns: DeliveryOutput['columns'],
  currencySymbol: string,
): Record<string, ReactNode> {
  return Object.fromEntries(
    columns.map((column) => {
      const metric = node.row.metrics[column.name];
      return [
        column.name,
        <Box
          key={column.name}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {metric === undefined
              ? '—'
              : formatMetricValue(metric.value, column.format, currencySymbol)}
          </Typography>
          {metric === undefined ? (
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>—</Typography>
          ) : (
            <MetricDelta delta={metric.delta} sentiment={metric.sentiment} />
          )}
        </Box>,
      ];
    }),
  );
}

export function DeliverySection({
  scope,
  creativeId,
  kpis,
  onNodeSelect,
}: DeliverySectionProps): ReactNode {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [popupNode, setPopupNode] = useState<DeliveryTreeNode | null>(null);
  const [copied, setCopied] = useState(false);
  const [popupMetricNames, setPopupMetricNames] = useState<[string] | [string, string]>(
    kpis.length >= 2 ? [kpis[0] ?? 'ctr', kpis[1] ?? 'cost'] : [kpis[0] ?? 'ctr'],
  );
  const delivery = useDelivery({ ...scope, creativeId, kpis }, true);

  function handleCopy(id: string): void {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_ICON_MS);
    });
  }

  const section = (body: ReactNode, actions?: ReactNode): ReactNode => (
    <Stack sx={{ gap: 3 }}>
      <SectionHeader title={SECTION_TITLE} caption={SECTION_CAPTION} actions={actions} />
      <MediaCard sx={{ overflow: 'hidden' }}>{body}</MediaCard>
    </Stack>
  );

  if (delivery.isError) return section(<ErrorState />);
  if (delivery.data === undefined) return section(<SectionSkeleton variant="table" rowCount={4} />);

  const tree = buildDeliveryTree(delivery.data.rows);

  if (tree.length === 0) return section(<NoData message="No delivery data for this creative." />);

  const toggle = (id: string): void => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allIds = expandableNodeIds(tree);
  const allExpanded = allIds.length > 0 && allIds.every((id) => expanded.has(id));
  const toggleAll = (): void => setExpanded(allExpanded ? new Set() : new Set(allIds));

  const selectNode = (node: DeliveryTreeNode): void => {
    onNodeSelect({ level: node.row.level, id: node.row.id });
    setPopupNode(node);
  };

  const { columns: metricDescriptors, currencySymbol } = delivery.data;
  const metricColumns = (align: NonNullable<MediaColumn['align']>): MediaColumn[] =>
    metricDescriptors.map((column) => ({
      key: column.name,
      label: column.label,
      width: '88px',
      align,
    }));

  const columns: MediaColumn[] = [
    { key: 'name', label: 'Campaign · Ad set · Ad', width: 'minmax(180px, 1fr)' },
    ...metricColumns('right'),
  ];

  const rows: MediaTableRow[] = visibleRows(tree, 0, expanded).map(({ node, depth }) => {
    const hasChildren = node.children.length > 0;
    return {
      id: node.row.id,
      onClick: () => selectNode(node),
      cells: {
        name: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: depth * 3, minWidth: 0 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                sx={{ width: EXPANDER_SIZE, height: EXPANDER_SIZE, flexShrink: 0 }}
                onClick={(event) => {
                  event.stopPropagation();
                  toggle(node.row.id);
                }}
              >
                <ChevronRightIcon
                  fontSize="small"
                  sx={{
                    transform: expanded.has(node.row.id) ? 'rotate(90deg)' : 'none',
                    transition: (theme) =>
                      theme.transitions.create('transform', {
                        duration: theme.transitions.duration.shortest,
                      }),
                  }}
                />
              </IconButton>
            ) : (
              <Box sx={{ width: EXPANDER_SIZE, flexShrink: 0 }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <NameCell name={node.row.name} />
              <Typography
                component="div"
                sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25, ...ELLIPSIS_SX }}
              >
                <span>{LEVEL_LABELS[node.row.level]}</span>
                {' · '}
                <span>ID {node.row.id}</span>
              </Typography>
            </Box>
          </Box>
        ),
        ...deliveryMetricCells(node, metricDescriptors, currencySymbol),
      },
    };
  });

  const childGroupLabel = popupNode === null ? null : CHILD_GROUP_LABELS[popupNode.row.level];
  const childColumns: MediaColumn[] = [
    { key: 'name', label: 'Name', width: 'minmax(160px, 1fr)', align: 'center' },
    ...metricColumns('center'),
  ];
  const childRows: MediaTableRow[] = (popupNode?.children ?? []).map((child) => ({
    id: child.row.id,
    cells: {
      name: <NameCell name={child.row.name} />,
      ...deliveryMetricCells(child, metricDescriptors, currencySymbol),
    },
  }));

  return (
    <>
      {section(
        <Box sx={{ overflowX: 'auto' }}>
          <MediaTable columns={columns} rows={rows} />
        </Box>,
        <Box
          component="button"
          type="button"
          onClick={toggleAll}
          sx={{
            bgcolor: 'transparent',
            border: 'none',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            color: 'primary.main',
            cursor: 'pointer',
            p: 0,
            transition: (theme) =>
              theme.transitions.create('opacity', {
                duration: theme.transitions.duration.shortest,
              }),
            '&:hover': { opacity: 0.7 },
          }}
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </Box>,
      )}
      <Dialog
        open={popupNode !== null}
        onClose={() => setPopupNode(null)}
        maxWidth={false}
        slotProps={{ paper: { sx: ZOOM_DIALOG_SX } }}
      >
        {popupNode !== null && (
          <DialogContent sx={{ p: 5 }}>
            <IconButton
              size="small"
              onClick={() => setPopupNode(null)}
              aria-label="Close"
              sx={{ position: 'absolute', top: 12, right: 12, color: 'text.secondary' }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Stack sx={{ gap: 4 }}>
              <Stack sx={{ gap: 1 }}>
                <Box
                  sx={(theme) => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    alignSelf: 'flex-start',
                    height: 20,
                    px: 2,
                    border: '1px solid',
                    borderColor: theme.palette.primary.main,
                    borderRadius: `${theme.radius.sm}px`,
                    color: 'primary.main',
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  })}
                >
                  {LEVEL_LABELS[popupNode.row.level]}
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{popupNode.row.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    ID {popupNode.row.id}
                  </Typography>
                  <Tooltip title={copied ? 'Copied' : 'Copy'} placement="top">
                    <IconButton size="small" onClick={() => handleCopy(popupNode.row.id)}>
                      <ContentCopyOutlined sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
              <Stack sx={{ gap: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <TrendMetricPicker
                    availableMetrics={kpis}
                    metricNames={popupMetricNames}
                    onMetricNamesChange={setPopupMetricNames}
                  />
                </Box>
                <CreativeTrendChart
                  scope={scope}
                  creativeId={creativeId}
                  node={{ level: popupNode.row.level, id: popupNode.row.id }}
                  metricNames={popupMetricNames}
                  legendAlign="center"
                />
              </Stack>
              {childGroupLabel !== null && childRows.length > 0 && (
                <Stack sx={{ gap: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    {childGroupLabel} inside this selection
                  </Typography>
                  <MediaCard
                    sx={{ overflow: 'hidden', width: 'fit-content', maxWidth: '100%', mx: 'auto' }}
                  >
                    <Box sx={{ overflowX: 'auto' }}>
                      <MediaTable columns={childColumns} rows={childRows} />
                    </Box>
                  </MediaCard>
                </Stack>
              )}
            </Stack>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
